// AppWrapper.js
import React, { ReactNode, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import ToSModal from "./Session/components/ToSModal";
import { fetchUser, setShowToSModal } from "../redux/slices/userSlice";
import { currentVersion } from "./terms_and_policies/TermsAndPolicies";
import { getAuth } from "firebase/auth";
import { setGAUser } from "../analytics";
import { fetchLogs } from "../redux/slices/changeLogsSlice";

interface AppWrapperProps {
  children: ReactNode; // This type allows for any renderable React content
}
// Global config like theme/lanugauage/timezone etc
const AppWrapper: React.FC<AppWrapperProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();

  const { status, user: tgptUser, isManualSignUp, showToSModal, isAcknowledged } = useSelector((state: { user }) => state.user);
  const { status: updateUserStatus } = useSelector((state: { updateFields }) => state.updateFields);

  // check if user has acknowledged latest version
  useEffect(() => {
    if (!isAcknowledged && status === "succeeded" && tgptUser && !tgptUser.tradegpt?.signed_agreement?.[currentVersion]) {
      dispatch(setShowToSModal(true));
    }
  }, [status, tgptUser, isAcknowledged]);

  // handle SSO signups - fetch the user again AFTER Firestore update is complete (to make sure user object is updated)
  useEffect(() => {
    if (!isManualSignUp && !tgptUser && updateUserStatus === "succeeded") {
      dispatch(fetchUser({}));
    }
  }, [updateUserStatus, isManualSignUp, tgptUser]);

  const auth = getAuth();

  const isUserSignedIn = useMemo(() => {
    return !!auth?.currentUser;
  }, [auth?.currentUser]);

  const user = useSelector((state: RootState) => state.auth.currentUser);

  useEffect(() => {
    if (isUserSignedIn && user?.userId) {
      setGAUser(user?.userId);
    }
  }, [isUserSignedIn, user]);

  const savedDarkModeSetting = localStorage.getItem("tradegpt-darkmode");

  useEffect(() => {
    const darkModePreference
      = savedDarkModeSetting !== null ? savedDarkModeSetting === "true" : true; // Default to true if no setting is saved
    document.body.className = darkModePreference ? "dark" : "light";
  }, [dispatch, savedDarkModeSetting]);

  useEffect(() => {
    dispatch(fetchLogs());
  }, []);
  return (
    <>
      {showToSModal && !isAcknowledged && <ToSModal />}
      {children}
    </>
  );
};

export default AppWrapper;
